import {complement, either, isEmpty, isNil, values, flatten} from 'ramda';

import {YesNoEmpty} from 'types/Forms';

export const isDefined = <T>(t: T | null | undefined): t is T => complement(isNil)(t);
export const isMissing = either(isEmpty, isNil);
export const hasItems = complement(isEmpty);

/**
 * Run the provided function with the entity as its argument if the entity is defined (not null or undefined).
 * Returns undefined otherwise.
 */
export const runIfDefined = <T, U>(func: (definedEntity: T) => U, entity: T | null | undefined): U | undefined =>
  isDefined(entity) ? func(entity) : undefined;

/**
 * Converts a Yes/No string to a boolean
 */
export const yesNoEmptyToBoolean = (value: YesNoEmpty) => value === 'yes';

/**
 * Converts a boolean to a Yes/No string
 */
export const booleanToYesNoEmpty = (value: boolean): YesNoEmpty =>
  value === true ? 'yes' : value === false ? 'no' : '';

export const hasValues = <T extends object>(obj?: T) => obj && hasItems(values(obj).filter((a) => !!a));

/**
 * Takes two collections `as` and `bs` and a predicate for joining an element
 * of `as` with an element of `bs`. Collects the resulting values of the
 * predicate given every combination of elements from `as` and elements from
 * `bs` and returns only those results that are not `false`.
 */
export const joinCollections =
  <A extends any, B extends any, C extends any>(pred: (a: A, b: B) => C | false) =>
  (as: ReadonlyArray<A>, bs: ReadonlyArray<B>): ReadonlyArray<C> =>
    flatten<ReadonlyArray<C>[]>(as.map((a) => bs.map((b) => pred(a, b)).filter((r): r is C => r !== false)));

export const groupBy = <T, K extends string = string>(fn: (a: T) => K, listArray: readonly T[]): Record<K, T[]> =>
  listArray.reduce(
    (prev, curr) => {
      const key = fn(curr);
      return {
        ...prev,
        [key]: [...(prev[key] || []), curr]
      };
    },
    {} as Record<K, T[]>
  );
